import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Button,
  Stack,
  Avatar,
  Card,
  CardContent,
  Box,
  IconButton,
  Collapse,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { PremiumDataGrid } from "../components/PremiumDataGrid";

import { useCollection } from "../hooks/useCollection";
import OtherProductDialog from "../components/OtherProductDialog";
import { OtherProduct } from "../interfaces/OtherProduct";
import { useAuth } from "../hooks/useAuth";
import { InventoryItem } from "../interfaces/Inventory";
import { isDrinkCategory } from "../utils/drinkUtils";

export default function OtherProductsPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    data: products,
    createItem,
    updateItem,
    deleteItem,
  } = useCollection<OtherProduct>(restaurantId ?? "", "otherProducts");

  const { data: categories = [] } = useCollection<{ id: string; name: string }>(
    restaurantId ?? "",
    "category"
  );

  const { data: inventoryItems = [] } = useCollection<InventoryItem>(
    restaurantId ?? "",
    "inventory"
  );

  const { canCreate, canEdit, canDelete } = useAuth();

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<OtherProduct | null>(null);

  /* ------------------ FILTER STATE ------------------ */
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [showCategories, setShowCategories] = useState(true);

  // Excluir bebidas de la vista "Otros Productos"
  const nonDrinkProducts = products.filter((p) => !isDrinkCategory(p.category));

  const filteredProducts = nonDrinkProducts.filter((p) => {
    const matchesName = p.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || p.category === selectedCategory;
    return matchesName && matchesCategory;
  });

  const nonDrinkCategories = categories.filter((c) => !isDrinkCategory(c.name));

  /* ------------------ DESKTOP COLUMNS ------------------ */
  const columns: GridColDef[] = [
    {
      field: "image",
      headerName: "Imagen",
      width: 100,
      renderCell: (params) => {
        return (
          <Avatar
            src={params.row.image}
            variant="rounded"
            sx={{ width: 56, height: 56 }}
          />
        );
      },
    },
    { field: "name", headerName: "Nombre", flex: 1 },
    { field: "category", headerName: "Categoría", flex: 1 },
    {
      field: "inventoryLink",
      headerName: "Inventario Vinculado",
      flex: 1,
      renderCell: (params) => {
        const product = params.row as OtherProduct;
        const invId = product.inventoryItemId;
        if (!invId) return <Typography variant="caption" color="text.disabled">No vinculado</Typography>;
        
        const invItem = inventoryItems.find(i => i.id === invId);
        return (
          <Chip 
            label={invItem?.name || "ID: " + invId.substring(0,6)} 
            size="small" 
            variant="outlined"
            color={invItem ? "success" : "default"}
          />
        );
      }
    },
    {
      field: "price",
      headerName: "Precio Base",
      width: 130,
      renderCell: (params) => {
        const prod = params.row as OtherProduct;
        return (
          <Typography variant="body2" fontWeight={700} color="success.main">
            ${(prod.price ?? 0).toLocaleString("es-CO")}
          </Typography>
        );
      },
    },
    {
      field: "variants",
      headerName: "Variaciones / Sabores",
      flex: 1.8,
      renderCell: (params) => {
        const prod = params.row as OtherProduct;
        if (!prod.variants || prod.variants.length === 0) {
          return (
            <Typography variant="caption" color="text.disabled">
              Sin variaciones
            </Typography>
          );
        }
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ py: 1, my: "auto" }}>
            {prod.variants.map((v) => (
              <Chip
                key={v.id || v.name}
                label={`${v.name}: $${((prod.price ?? 0) + (v.price ?? 0)).toLocaleString("es-CO")}`}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ fontSize: "0.72rem", height: 22 }}
              />
            ))}
          </Stack>
        );
      },
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 160,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            disabled={!canEdit}
            onClick={() => {
              setCurrent(params.row as OtherProduct);
              setOpen(true);
            }}
          >
            Editar
          </Button>
          <Button
            size="small"
            color="error"
            disabled={!canDelete}
            onClick={() => {
              if (window.confirm(`¿Estás seguro de que deseas eliminar "${params.row.name}"?`)) {
                deleteItem(params.row.id);
              }
            }}
          >
            Eliminar
          </Button>
        </Stack>
      ),
    },
  ];

  /* ------------------ HEADER ------------------ */
  const Header = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      justifyContent="space-between"
      mb={2}
    >
      <Typography variant="h5">🍔 Otros Productos</Typography>

      {canCreate && (
        <Button
          variant="contained"
          onClick={() => {
            setCurrent(null);
            setOpen(true);
          }}
        >
          Agregar producto
        </Button>
      )}
    </Stack>
  );

  /* ------------------ FILTER BAR ------------------ */
  const FilterBar = (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" mb={1}>
        <Button
          size="small"
          onClick={() => setShowCategories((p) => !p)}
          endIcon={
            <ExpandMoreIcon
              sx={{
                transform: showCategories ? "rotate(180deg)" : "none",
                transition: "0.2s",
              }}
            />
          }
        >
          Categorías
        </Button>

        {selectedCategory !== "all" && (
          <Box
            onClick={() => setSelectedCategory("all")}
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: "20px",
              border: "1.5px solid",
              borderColor: "error.light",
              bgcolor: "transparent",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "0.78rem",
              color: "error.main",
              transition: "all 0.18s ease",
              "&:hover": { bgcolor: "error.main", color: "#fff" },
            }}
          >
            ✕ Limpiar
          </Box>
        )}
      </Stack>

      <Collapse in={showCategories} timeout={250}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap pb={0.5}>
          {[{ id: "all", name: "Todas" }, ...nonDrinkCategories].map((cat) => {
            const filterValue = cat.id === "all" ? "all" : cat.name;
            const active = selectedCategory === filterValue;
            return (
              <Box
                key={cat.id}
                component="button"
                onClick={() => setSelectedCategory(cat.id === "all" ? "all" : cat.name)}
                sx={{
                  px: 2,
                  py: 0.75,
                  borderRadius: "20px",
                  border: "1.5px solid",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.83rem",
                  transition: "all 0.18s ease",
                  borderColor: active ? "primary.main" : "divider",
                  bgcolor: active ? "primary.main" : "background.paper",
                  color: active ? "#fff" : "text.secondary",
                  "&:hover": {
                    borderColor: "primary.main",
                    color: active ? "#fff" : "primary.main",
                  },
                }}
              >
                {cat.name}
              </Box>
            );
          })}
        </Stack>
      </Collapse>
    </Box>
  );

  /* ------------------ MOBILE CARDS ------------------ */
  const MobileList = (
    <Stack spacing={2}>
      {filteredProducts.map((product) => {
        return (
          <Card key={product.id} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={product.image}
                  variant="rounded"
                  sx={{ width: 64, height: 64, borderRadius: 2 }}
                />

                <Box flex={1}>
                  <Typography fontWeight={700}>{product.name}</Typography>

                  <Typography variant="body2" color="text.secondary">
                    {product.category}
                  </Typography>

                  <Typography variant="body2" fontWeight={800} color="success.main">
                    ${(product.price ?? 0).toLocaleString("es-CO")}
                  </Typography>
                </Box>

                <Stack direction="row">
                  {canEdit && (
                    <IconButton
                      onClick={() => {
                        setCurrent(product);
                        setOpen(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                  {canDelete && (
                    <IconButton
                      color="error"
                      onClick={() => {
                        if (window.confirm(`¿Estás seguro de que deseas eliminar "${product.name}"?`)) {
                          deleteItem(product.id!);
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        );
      })}

      {filteredProducts.length === 0 && (
        <Box
          sx={{
            py: 4,
            textAlign: "center",
            color: "text.secondary",
            fontSize: "0.9rem",
          }}
        >
          No se encontraron productos.
        </Box>
      )}
    </Stack>
  );

  return (
    <>
      {Header}
      {FilterBar}

      {!isMobile && (
        <Box sx={{ height: 520 }}>
          <PremiumDataGrid
            rows={filteredProducts}
            columns={columns}
            getRowId={(row) => row.id!}
            getRowHeight={() => "auto"}
            onRowDoubleClick={(p: GridRowParams) => {
              setCurrent(p.row as OtherProduct);
              setOpen(true);
            }}
          />
        </Box>
      )}

      {isMobile && MobileList}

      <OtherProductDialog
        key={current?.id ?? "new"}
        open={open}
        onClose={() => setOpen(false)}
        categories={nonDrinkCategories}
        initial={
          current ?? {
            name: "",
            category: nonDrinkCategories[0]?.name || "",
            description: "",
            price: 0,
            active: true,
            image: "",
          }
        }
        onSubmit={async (values) => {
          if (current?.id) await updateItem(current.id, values);
          else await createItem(values);
          setOpen(false);
        }}
      />
    </>
  );
}
